import React, { useEffect, useMemo, useState } from "react";
import { usePaginatedApplicantsWithoutATS } from "../../../../data/applicants";
import GreenUnderlineHeading from "../../../custom-ui/headings/heading";
import { useParams } from "react-router-dom";
import { useJobByIdForNewEmp } from "../../../../data/listings";
import ApplyNowCounterSeekerList from "./ApplyNowMetrics/ApplyNowCounterSeekerList";
import DownloadIconWithLabel from "../../../dashboard/AdminMetricDashboard/DownLoadIcon";

const PAGE_SIZE = 10;

const ApplicantsList = () => {
  const params = useParams();
  const jobId = params.id ?? "";

  const { data }:any = useJobByIdForNewEmp(params.id);


  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);

  const { applicants, resetAndFetch, loading } =
    usePaginatedApplicantsWithoutATS(jobId);

  useEffect(() => {
    if (jobId) {
      resetAndFetch();
    }
  }, [jobId]);

  const filteredApplicants = useMemo(() => {
    if (!search.trim()) return applicants;
    const keyword = search.trim().toLowerCase();

    return applicants.filter((applicant: any) => {
      const name = applicant.applicantName?.toLowerCase() || "";
      const email = applicant.applicantEmail?.toLowerCase() || "";
      return name.includes(keyword) || email.includes(keyword);
    });
  }, [applicants, search]);

  const paginatedApplicants = filteredApplicants.slice(
    (page - 1) * PAGE_SIZE,
    page * PAGE_SIZE,
  );

  const exportToCSV = (data: any[]) => {
    if (!data || data.length === 0) return;

    const headers = ["Name", "Email", "Phone", "Applied On", "Resume Link"];
    const csvRows = [
      headers.join(","),
      ...data.map((row) => {
        const applyDate =
          row.applyDate?.toDate?.() instanceof Date
            ? row.applyDate.toDate().toLocaleDateString()
            : new Date(row.applyDate).toLocaleDateString();

        return [
          `"${row.applicantName || ""}"`,
          `"${row.applicantEmail || ""}"`,
          `"${row.applicantPhone || ""}"`,
          `"${applyDate || ""}"`,
          `"${row.resumeLink || ""}"`,
        ].join(",");
      }),
    ];

    const blob = new Blob([csvRows.join("\n")], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `Applicants_List_${jobId}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

    if (data?.noATS === false) {
    return <ApplyNowCounterSeekerList  companyFullName={data?.companyFullName} jobName={data?.jobName}  id={ data?.id || ""} />
  }

  return (
    <div className="ml-4 w-full">
      <div className="pr-12 pb-5 w-full">
        <GreenUnderlineHeading title="Applicants" />
      </div>
      <div className="pr-12 pb-5 flex justify-end items-center gap-4">
        <div
          className="cursor-pointer"
          onClick={() => exportToCSV(filteredApplicants)}
        >
          <DownloadIconWithLabel
            tooltipText="Download Applicants List"
            viewDown={true}
          />
        </div>

        <div className="relative w-full max-w-sm">
          <div className="pl-3 absolute inset-y-0 start-0 flex items-center ps-3 pointer-events-none">
            <svg
              className="w-4 h-4 text-gray-500"
              aria-hidden="true"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 20 20"
            >
              <path
                stroke="currentColor"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="m19 19-4-4m0-7A7 7 0 1 1 1 8a7 7 0 0 1 14 0Z"
              />
            </svg>
          </div>
          <input
            type="text"
            placeholder="Search Applicants..."
            value={searchInput}
            onChange={(e) => {
              const value = e.target.value;
              setSearchInput(value);
              setSearch(value.trim());
              setPage(1);
            }}
            className="pl-10 pr-4 py-2 text-md w-full border rounded-md"
          />
        </div>
      </div>

      <div className="min-w-full mb-4 pr-12">
        <table className="w-full bg-white shadow-md rounded-md">
          <thead className="border-b">
            <tr>
              <th className="text-sm text-gray-900 px-6 py-4 text-left">
                Name
              </th>
              <th className="text-sm text-gray-900 px-6 py-4 text-left">
                Email
              </th>
              <th className="text-sm text-gray-900 px-6 py-4 text-left">
                Phone
              </th>
              <th className="text-sm text-gray-900 px-6 py-4 text-left">
                Applied On
              </th>
              <th className="text-sm text-gray-900 px-6 py-4 text-left">
                Resume
              </th>
            </tr>
          </thead>
          <tbody>
            {paginatedApplicants.length > 0 ? (
              paginatedApplicants.map((applicant: any) => (
                <tr key={applicant.id} className="border-b">
                  <td className="px-6 py-4">{applicant.applicantName}</td>
                  <td className="px-6 py-4">{applicant.applicantEmail}</td>
                  <td className="px-6 py-4">{applicant.applicantPhone}</td>
                  <td className="px-6 py-4">
                    {applicant.applyDate
                      ? new Date(
                          applicant.applyDate?.toDate?.() ||
                            applicant.applyDate,
                        ).toLocaleDateString()
                      : "—"}
                  </td>
                  <td className="px-6 py-4">
                    {applicant.resumeLink ? (
                      <a
                        href={applicant.resumeLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 underline"
                      >
                        View Resume
                      </a>
                    ) : (
                      "—"
                    )}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td className="px-6 py-4" colSpan={5}>
                  {loading ? "Loading..." : "No applicants found."}
                </td>
              </tr>
            )}
          </tbody>
        </table>

        {filteredApplicants.length > 0 && (
          <div className="mt-4 flex justify-center items-center text-sm">@
            <button
              onClick={() => setPage((prev: any) => Math.max(prev - 1, 1))}
              disabled={page === 1}
              className="bg-[#19405e] hover:bg-[#19405e] m-2 px-3 py-1 rounded disabled:opacity-50 text-white"
            >
              Prev
            </button>
            <span className="text-gray-700 m-3">Page {page}</span>
            <button
              onClick={() =>
                setPage((prev: any) =>
                  prev * PAGE_SIZE < filteredApplicants.length
                    ? prev + 1
                    : prev,
                )
              }
              disabled={page * PAGE_SIZE >= filteredApplicants.length}
              className="bg-[#19405e] hover:bg-[#19405e] m-2 px-3 py-1 rounded disabled:opacity-50 text-white"
            >
              Next
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ApplicantsList;
